import { useEffect, useMemo, useState } from 'react';
import { Box, Slide } from '@mui/material';
import { TransitionGroup } from 'react-transition-group';

import { ParamsType } from '../../context/NavigatorContext.interface';
import { useNavigationState, useNavigationUpdate, NavigationUpdateContext } from '../../context';

import { ScreensType } from '.';

const TRANSITION_TIME = 400;

/**
 * Determines transition direction based on navigation state and screen index.
 */
function getTransitionDirection(index: number, stackLength: number, navDirection: 'left' | 'right'): 'left' | 'right' {
    const isCurrentScreen = index === stackLength - 1;
    const isPreviousScreen = index === stackLength - 2;

    if (isCurrentScreen) {
        return navDirection;
    }
    if (isPreviousScreen && navDirection === 'left') {
        return 'right';
    }
    return 'left';
}

/**
 * SlidingStackRenderer - Handles screen navigation with animations.
 */
function SlidingStackRenderer({ screens }: { screens: ScreensType }) {
    const { stack, currentScreen, params } = useNavigationState();
    const { navigateToPage, goBack, push, pop } = useNavigationUpdate();
    const [navDirection, setNavDirection] = useState<'left' | 'right'>('left');
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Handles transition state to prevent actions during animations
    useEffect(() => {
        if (isTransitioning) {
            const timeoutId = setTimeout(() => setIsTransitioning(false), TRANSITION_TIME);
            return () => clearTimeout(timeoutId);
        }
    }, [isTransitioning]);

    // Handles navigation to a new screen
    const handleNavigate = (screen: string, screenParams?: ParamsType) => {
        console.log('navigate', screen, screenParams, params, currentScreen);
        if (isTransitioning || (screen === currentScreen && params === screenParams)) {
            return;
        }
        setNavDirection('left');
        setIsTransitioning(true);
        navigateToPage(screen, screenParams);
    };

    // Handles returning to the previous screen
    const handleGoBack = () => {
        if (isTransitioning || stack.length <= 1) {
            return;
        }
        setNavDirection('right');
        setIsTransitioning(true);
        goBack();
    };

    // Memoized navigation context value
    const navigationUpdateValue = useMemo(
        () => ({
            navigateToPage: handleNavigate,
            goBack: handleGoBack,
            push,
            pop,
        }),
        [handleNavigate, handleGoBack, push, pop]
    );

    // Precompute rendered screens
    const renderedScreens = useMemo(
        () =>
            stack.map((screen, index) => {
                const ScreenComp = screens[screen.screen].component;
                const direction = getTransitionDirection(index, stack.length, navDirection);
                const isVisible = index === stack.length - 1 || index === stack.length - 2;

                return (
                    <Slide
                        key={`${screen.screen}${screen.params?.id || ''}`}
                        mountOnEnter
                        unmountOnExit
                        timeout={TRANSITION_TIME}
                        direction={direction}>
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '100%',
                                minHeight: '100%',
                                inset: 0,
                                zIndex: index === stack.length - 1 ? 2 : 1,
                                display: isVisible ? 'block' : 'none',
                            }}>
                            <ScreenComp {...screen.params} />
                        </Box>
                    </Slide>
                );
            }),
        [stack, screens, navDirection]
    );

    // Render the navigation context and screen transitions
    return (
        <NavigationUpdateContext.Provider value={navigationUpdateValue}>
            <Box sx={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
                <TransitionGroup>{renderedScreens}</TransitionGroup>
            </Box>
        </NavigationUpdateContext.Provider>
    );
}

export default SlidingStackRenderer;
